import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAnalytics } from '../context/AnalyticsContext'
import { useAuth } from '../context/AuthContext'
import { getZodiacSign } from '../utils/zodiac'
import { resolvePhotoUrls } from '../lib/profilePhotos'
import Chip from './Chip'
import Loader from './Loader'
import './Profile.css'

function parseBirthDate(dateStr) {
  if (!dateStr) return new Date(NaN)
  const s = String(dateStr).trim()
  if (s.includes('T')) return new Date(s)
  return new Date(s + 'T00:00:00')
}

const Profile = ({ onEditingChange }) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { trackEvent } = useAnalytics()
  const { user, profile, refreshProfile } = useAuth()
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0)
  
  const GENDER_LABELS = { male: t('profile.genderMan'), female: t('profile.genderWoman') }
  const LOOKING_FOR_LABELS = { male: t('profile.lookingForMen'), female: t('profile.lookingForWomen'), both: t('profile.lookingForEveryone') }

  useEffect(() => {
    setCurrentPhotoIndex(0)
  }, [profile?.photos?.length])

  if (!user || !profile) {
    return (
      <div className="profile-screen">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
          <Loader text={t('common.loading')} />
        </div>
      </div>
    )
  }

  const photoUrls = resolvePhotoUrls(profile.photos || [], profile.updated_at || '')
  const dob = profile.date_of_birth
  let zodiac = { name: 'Unknown', symbol: '?' }
  if (dob) {
    const d = parseBirthDate(dob)
    if (!isNaN(d.getTime())) zodiac = getZodiacSign(d.getDate(), d.getMonth() + 1)
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return ''
    const d = parseBirthDate(dateStr)
    if (isNaN(d.getTime())) return ''
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
  }

  const formatTime = (timeStr) => {
    if (!timeStr) return ''
    if (typeof timeStr === 'string' && timeStr.includes(':')) return timeStr
    return timeStr || ''
  }

  const handleGetAstroPlus = () => {
    trackEvent('get_astro_plus_clicked', { location: 'profile' })
    navigate('/paywall?reason=subscription', { state: { from: window.location.pathname || '/app' } })
  }

  const handleEdit = (section) => {
    onEditingChange?.(true)
    navigate(`/app/profile/edit/${section}`)
  }

  const handleSettings = () => {
    navigate('/app/profile/settings')
  }

  const handlePrevPhoto = () => {
    if (currentPhotoIndex > 0) {
      setCurrentPhotoIndex((i) => i - 1)
    }
  }

  const handleNextPhoto = () => {
    if (currentPhotoIndex < photoUrls.length - 1) {
      setCurrentPhotoIndex((i) => i + 1)
    }
  }

  return (
    <div className="profile-screen">
      <div className="profile-top-bar">
        <button type="button" className="logo-large logo-btn" onClick={() => navigate('/app')} aria-label="Go to Explore">
          <img src="/Logo.png" alt="AstroMatch" />
        </button>
        {!(profile?.subscription_status === 'active' || profile?.subscription_status === 'trial') && (
          <button type="button" className="astro-plus-btn" onClick={handleGetAstroPlus}>
            {t('profile.getAstroPlus')}
          </button>
        )}
      </div>

      <div className="profile-content">
        <div className="profile-photo-block">
          {photoUrls.length > 0 && photoUrls[currentPhotoIndex] ? (
            <>
              {photoUrls.length > 1 && (
                <>
                  {currentPhotoIndex > 0 && (
                    <button
                      type="button"
                      className="profile-photo-nav-btn profile-photo-nav-btn-left"
                      onClick={handlePrevPhoto}
                      aria-label={t('common.previousPhoto')}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="15 18 9 12 15 6" />
                      </svg>
                    </button>
                  )}
                  {currentPhotoIndex < photoUrls.length - 1 && (
                    <button
                      type="button"
                      className="profile-photo-nav-btn profile-photo-nav-btn-right"
                      onClick={handleNextPhoto}
                      aria-label={t('common.nextPhoto')}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </button>
                  )}
                </>
              )}
              <img src={photoUrls[currentPhotoIndex]} alt="Profile" />
              <button
                type="button"
                className="profile-photo-edit-btn"
                onClick={(e) => { e.stopPropagation(); handleEdit('photos') }}
                title={t('common.editPhotos')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </button>
              {photoUrls.length > 1 && (
                <div className="photo-indicators">
                  {photoUrls.map((_, i) => (
                    <span key={i} className={`photo-indicator ${i === currentPhotoIndex ? 'active' : ''}`} />
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="photo-placeholder">{t('common.noPhoto')}</div>
              <button
                type="button"
                className="profile-photo-edit-btn"
                onClick={() => handleEdit('photos')}
                title={t('common.editPhotos')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </button>
            </>
          )}
        </div>

        <div className="profile-row profile-row-name">
          <h2 className="profile-name">{profile.name || '—'}</h2>
          <button type="button" className="profile-row-edit" onClick={() => handleEdit('name')}>
            {t('common.edit')}
          </button>
        </div>
        <p className="profile-zodiac">{zodiac.name} {zodiac.symbol}</p>

        <div className="profile-section">
          <div className="profile-row">
            <span className="profile-section-label">{t('profile.dateTimePlace')}</span>
            <button type="button" className="profile-row-edit" onClick={() => handleEdit('birth')}>
              {t('common.edit')}
            </button>
          </div>
          <div className="profile-details">
            <div className="detail-item">
              <span className="detail-label">{t('profile.dateOfBirth')}</span>
              <span className="detail-value">{formatDate(dob)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">{t('profile.timeOfBirth')}</span>
              <span className="detail-value">{formatTime(profile.time_of_birth) || '—'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">{t('profile.placeOfBirth')}</span>
              <span className="detail-value">{profile.place_of_birth || '—'}</span>
            </div>
          </div>
        </div>

        <div className="profile-section">
          <div className="profile-row">
            <span className="profile-section-label">{t('profile.interests')}</span>
            <button type="button" className="profile-row-edit" onClick={() => handleEdit('interests')}>
              {t('common.edit')}
            </button>
          </div>
          {profile.interests && profile.interests.length > 0 ? (
            <div className="interests-chips">
              {profile.interests.map((interest, i) => {
                const tr = t('onboarding.interestsItems.' + interest)
                const label = tr === 'onboarding.interestsItems.' + interest ? interest : tr
                return <Chip key={i} active={false}>{label}</Chip>
              })}
            </div>
          ) : (
            <p className="profile-section-empty">{t('profile.noInterests')}</p>
          )}
        </div>

        <div className="profile-section">
          <span className="profile-section-label">{t('profile.gender')}</span>
          <p className="detail-value">{GENDER_LABELS[profile.gender] || profile.gender}</p>
        </div>

        <div className="profile-section">
          <span className="profile-section-label">{t('profile.lookingFor')}</span>
          <p className="detail-value">{LOOKING_FOR_LABELS[profile.looking_for] || profile.looking_for}</p>
        </div>

        <button type="button" className="profile-settings-btn" onClick={handleSettings}>
          {t('common.settings')}
        </button>
      </div>
    </div>
  )
}

export default Profile
